import { useQuery } from '@tanstack/react-query';
import { FlaskConical, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { DatasetListDto, PaginatedResponse } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { CreateDatasetSheet } from '@/UI/Simulation/components/CreateDataset.sheet';
import { getScoreColor, MAX_DATASETS } from '@/UI/Simulation/components/simulationUtils';

type Props = {
  onSelect: (id: number) => void;
};

export const DatasetListView = ({ onSelect }: Props) => {
  const { t } = useTranslation('simulation_page');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useQuery<PaginatedResponse<DatasetListDto>>({
    queryKey: [QueryKey.simulation.datasets, 0, MAX_DATASETS],
  });
  const datasets = data?.items;
  const atCap = (data?.total ?? 0) >= MAX_DATASETS;

  return (
    <div className="space-y-6" data-testid="dataset-list-view">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{t('title')}</h1>
        <Button
          data-testid="create-dataset-button"
          size="sm"
          onClick={() => setShowCreate(true)}
          disabled={atCap}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          {t('create_dataset')}
        </Button>
      </div>

      {atCap && (
        <p
          data-testid="dataset-list-cap-warning"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          {t('dataset_count_max_exceeded', { max: MAX_DATASETS })}
        </p>
      )}

      {isLoading ? (
        <div data-testid="dataset-list-loading" className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !datasets?.length ? (
        <div
          data-testid="dataset-list-empty"
          className="rounded-lg border border-dashed p-12 text-center"
        >
          <FlaskConical className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">{t('empty_state')}</p>
          <Button size="sm" className="mt-4" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t('create_dataset')}
          </Button>
        </div>
      ) : (
        <div data-testid="dataset-list-items" className="grid gap-3">
          {datasets.map((ds) => (
            <button
              key={ds.id}
              data-testid={`dataset-item-${ds.id}`}
              onClick={() => onSelect(ds.id)}
              className="flex cursor-pointer items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50"
            >
              <div>
                <p className="text-sm font-medium">{ds.name}</p>
                {ds.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{ds.description}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {ds.latest_run_score != null && (
                  <span
                    data-testid={`dataset-item-score-${ds.id}`}
                    className={`text-xs font-semibold ${getScoreColor(ds.latest_run_score)}`}
                  >
                    {ds.latest_run_score.toFixed(1)}/5
                  </span>
                )}
                {ds.latest_run_manual_score != null && (
                  <span
                    data-testid={`dataset-item-manual-score-${ds.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    {Math.round((ds.latest_run_manual_score / 5) * 100)}%
                  </span>
                )}
                <Badge variant="secondary">{t('runs_count', { count: ds.run_count })}</Badge>
                <Badge variant="secondary">
                  {t('messages_count', { count: ds.message_count })}
                </Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      <CreateDatasetSheet open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
};
